/**
 * Travel QR Journey API Service
 *
 * Uses the existing SecureClient from es-checkout-journey but with
 * the payload shapes and routes required by the travel journey flow.
 *
 * Environment variables needed (add to your .env files):
 *   REACT_APP_SENDOTPLOGIN
 *   REACT_APP_VERIFYOTPLOGIN
 *   REACT_APP_TRAVEL_QEC_INGESTION_URL
 */

import { SecureClient } from "./index";
import DataLakeTracker from "./tracking/data-lake-tracker";
import { v4 as uuidv4 } from "uuid";

// ---------------------------------------------------------------------------
// Helpers (inline copies of travel-qr's Helper.js utilities)
// ---------------------------------------------------------------------------

export function isApiBodySuccess(res) {
  if (!res) return false;
  const code = res?.statusCode ?? res?.status ?? res?.code;
  if (code === undefined || code === null) {
    return !!(res?.body || res?.data || res?.token);
  }
  if (typeof code === "number") return code >= 200 && code < 300;
  if (typeof code === "string") {
    const num = parseInt(code, 10);
    if (!isNaN(num)) return num >= 200 && num < 300;
    return code.toUpperCase() === "SUCCESS";
  }
  return false;
}

export function extractApiError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;

  if (typeof err === "object" && !err.response) {
    const msg =
      err?.statusMsg ||
      err?.message ||
      err?.statusDes ||
      err?.error ||
      err?.errors?.[0]?.message;
    if (msg && typeof msg === "string") return msg;
  }

  const responseData = err?.response?.data;
  const msg =
    responseData?.statusMsg ||
    responseData?.message ||
    responseData?.statusDes ||
    responseData?.error ||
    responseData?.errors?.[0]?.message ||
    err?.message;

  if (msg && typeof msg === "string" && msg !== "Network Error") return msg;

  if (err?.message === "Network Error" || err?.code === "ECONNABORTED") {
    return "Network error. Please check your connection and try again.";
  }

  const status = err?.response?.status;
  if (status === 400) return "Invalid request. Please check your details.";
  if (status === 401) return "Session expired. Please refresh and try again.";
  if (status === 403) return "Access denied. Please try again.";
  if (status === 429) return "Too many attempts. Please wait a moment and try again.";

  return fallback;
}

// ---------------------------------------------------------------------------
// Cookie helper (store token after OTP verify)
// ---------------------------------------------------------------------------

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

function getCookie(name) {
  const cookieArray = document.cookie.split(";");
  for (let i = 0; i < cookieArray.length; i++) {
    const cookiePair = cookieArray[i].trim().split("=");
    if (cookiePair[0] === name) {
      return decodeURIComponent(cookiePair[1]);
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Travel Journey API calls
// ---------------------------------------------------------------------------

const SEND_OTP_URL = process.env.REACT_APP_SENDOTPLOGIN;
const VERIFY_OTP_URL = process.env.REACT_APP_VERIFYOTPLOGIN;
const QEC_INGESTION_URL = process.env.REACT_APP_TRAVEL_QEC_INGESTION_URL;

export const travelSendOtp = ({
  mobile,
  merchantId,
  userId,
  isResendOtp = false,
  requestId,
  successFn,
  errorFn,
  finallyFn,
}) => {
  DataLakeTracker({
    logEventType: "device_info",
    eventName: "send_otp_button_clicked",
    eventValue: uuidv4(),
    Authorization: null,
    customerId: null,
    customerRefNum: null,
    finallyFn: () => {},
  });
  SecureClient({
    isEncrypted: true,
    isExternalBaseUrl: true,
    externalBaseUrl: SEND_OTP_URL,
    route: SEND_OTP_URL,
    method: "POST",
    payload: {
      mobileNumber: mobile,
      templateType: "OTP_QR",
      isResendOtp,
      merchantId,
      userId,
    },
    otherPayloadDetailsPresent: true,
    requestId,
    successFn: (res) => {
      if (!isApiBodySuccess(res)) {
        if (errorFn) errorFn(res);
        return;
      }
      if (successFn) successFn(res);
    },
    errorFn,
    finallyFn,
  });
};

export const travelVerifyOtp = ({
  mobile,
  otp,
  utmResource,
  medium,
  userId,
  successFn,
  errorFn,
  finallyFn,
}) => {
  DataLakeTracker({
    logEventType: "device_info",
    eventName: "verify_otp_button_clicked",
    eventValue: uuidv4(),
    Authorization: null,
    customerId: null,
    customerRefNum: null,
    finallyFn: () => {},
  });
  SecureClient({
    isEncrypted: true,
    isExternalBaseUrl: true,
    externalBaseUrl: VERIFY_OTP_URL,
    route: VERIFY_OTP_URL,
    method: "POST",
    payload: {
      source: "WEB",
      medium: medium,
      templateType: "OTP_QR",
      sourceName: "CHECKOUT",
      deviceType: "desktop",
      mobileNumber: mobile,
      otpCode: otp,
      utmResource,
      adGroupId: userId,
    },
    otherPayloadDetailsPresent: true,
    successFn: (res) => {
      if (!isApiBodySuccess(res)) {
        const bodyError = new Error(
          extractApiError(res, "OTP verification failed. Please try again.")
        );
        bodyError.bodyResponse = res;
        if (errorFn) errorFn(bodyError);
        return;
      }
      const token = res?.body?.token;
      if (token) {
        setCookie("token", token, 1);
      }
      if (successFn) successFn(res);
    },
    errorFn,
    finallyFn,
  });
};

export const travelQecProfileIngestion = ({ payload, successFn, errorFn, finallyFn }) => {
  SecureClient({
    isEncrypted: true,
    isExternalBaseUrl: true,
    externalBaseUrl: QEC_INGESTION_URL,
    route: QEC_INGESTION_URL,
    method: "POST",
    headers: {
      authorization: getCookie("token"),
    },
    payload,
    successFn: (res) => {
      if (!isApiBodySuccess(res)) {
        if (errorFn) errorFn(res);
        return;
      }
      if (successFn) successFn(res);
    },
    errorFn,
    finallyFn,
  });
};
